<template>
  <div>
    <div ref="col">
      <TransitionScroller ref="virtualScrollContainer" :id="`scroller_cp_${cpData._id}`">
        <div
          style="position: relative"
          :class="{
            'margin-roundbar': UI.showSidebar,
          }"
        >
          <AnnotationBox
            v-for="ann in cpData.annotations"
            :key="`ann${ann._id}`"
            :id="`annotation-${ann._id}`"
            :ref="`annotation-${ann._id}`"
            :data-round="cpData.round"
            :style="annotationBoxStyle(ann)"
            :annotation="ann"
            :disabled="isAnnotationDisabled(cpData, ann)"
            :parentCounterpoint="cpData"
            :author="cpData.author.name"
            @resized="doAnnotationResized"
            @hoverIn="annotationHoverIn"
            @hoverOut="annotationHoverOut"
            @click="doAnnotationClick($event, ann)"
            @geometryUpdated="annotationGeometryUpdated"
            @geometriesUpdated="annotationParagraphsGeometriesUpdated(ann, $event)"
            @mainTextChanged="updateAnnotationTempText(ann, $event)"
          />
        </div>
      </TransitionScroller>
    </div>
  </div>
</template>

<script>
import AnnotationBox from '@/components/annotations/AnnotationBoxRefactored.vue';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import TransitionScroller from '../general/TransitionScroller';
import _ from 'lodash';
import { fixedRowHeight } from '@/assets/js/constants';

export default {
  name: 'DebateRound',
  components: {
    AnnotationBox,
    TransitionScroller,
  },
  props: {
    cpData: Object,
    scrollerContainerId: String,
  },

  data() {
    return {
      editMode: 'add',
      newAnnotationId: null,
      scrollTop: 0,
      currentGeometries: {},
      geometries: {},
    };
  },

  computed: {
    fixedRowHeight() {
      return fixedRowHeight;
    },

    ...mapState(['counterpointData', 'annotationHoverIds', 'mainPanelView', 'UI']),

    ...mapGetters([
      'isAnnotationDisabled',
      'showingOutlinePanel',
      'useExcerpts',
      'userId',
      'myLatestRoundNumber',
    ]),
  },

  mounted() {
    this.setAnnotationCitations();
    this.updateGeometries();
    this.checkEmptyAnnotation();
    this.$emit('mounted');
  },

  updated() {
    this.setAnnotationCitations();
    this.updateGeometries();
    this.checkEmptyAnnotation();
    this.$emit('updated');
  },
  created() {
    window.addEventListener('resize', this.myEventHandler);
  },
  unmounted() {
    window.removeEventListener('resize', this.myEventHandler);
    this.setEmptyAnnotation(false);
  },
  methods: {
    ...mapActions(['annotationHoverIn', 'annotationHoverOut']),

    ...mapMutations(['setEmptyAnnotation']),

    safeStringify(value) {
      const cache = new Set();
      try {
        return JSON.stringify(value, (key, val) => {
          if (typeof val === 'object' && val !== null) {
            if (cache.has(val)) return '[Circular]';
            cache.add(val);
          }
          if (typeof val === 'bigint') return `__bigint:${val.toString()}`;
          return val;
        });
      } catch (error) {
        return `"[StringifyError:${error?.message || 'unknown'}]"`;
      }
    },

    logDebug(label, payload) {
      const message = this.safeStringify(payload);
      console.debug(`[DebateRound] ${label}`, message);
    },

    checkEmptyAnnotation() {
      if (this.cpData.round === this.myLatestRoundNumber) {
        let empty = false;
        this.cpData.annotations
          // validate only current user annotations
          .filter(ann => ann.author?._id === this.userId)
          .map(data => {
            let comment = document.querySelector(`#annotation-${data._id} .ProseMirror`)?.innerHTML;
            if (!comment?.replace(/(<([^>]+)>)/gi, '').trim() && comment !== undefined) {
              empty = true;
            }
          });
        this.setEmptyAnnotation(empty);
      }
    },

    updateAnnotationTempText(ann, text) {
      ann.updatedText = text;
    },

    myEventHandler() {
      this.$forceUpdate();
    },

    updateGeometries() {
      const geometries = this.geometries;
      if (!_.isEqual(this.currentGeometries, geometries)) {
        this.currentGeometries = geometries;
        const geometryKeys = Object.keys(geometries || {});
        const firstGeometryKey = geometryKeys[0];
        const firstGeometry = firstGeometryKey ? geometries[firstGeometryKey] : null;
        const scrollerEl =
          this.$refs.virtualScrollContainer && this.$refs.virtualScrollContainer.$el;
        const scrollerTopStyle = scrollerEl ? scrollerEl.style.top : undefined;
        const parsedScrollerTop =
          typeof scrollerTopStyle === 'string' ? parseFloat(scrollerTopStyle) : undefined;
        this.logDebug('geometriesUpdated', {
          cpId: this.cpData._id,
          round: this.cpData.round,
          annotationCount: this.cpData.annotations?.length,
          geometryCount: geometryKeys.length,
          sampleTop: firstGeometry?.top,
          sampleLeft: firstGeometry?.left,
          scrollerTopStyle,
          parsedScrollerTop,
        });
        this.$emit('geometriesUpdated', {
          geometries: this.currentGeometries,
          cpId: this.cpData._id,
          cpRefEl: this.$refs.col,
          cpScroller: this.$refs.virtualScrollContainer,
        });
      }
    },

    getScroller() {
      return this.$refs.virtualScrollContainer;
    },

    doAnnotationResized() {
      this.$forceUpdate();
    },

    doAnnotationClick(ev, ann) {
      this.$emit('annotationClicked', {
        event: ev,
        annotation: ann,
        selection: window.getSelection(),
        counterpoint: this.cpData,
        ref: this.$refs[`annotation-${ann._id}`][0],
      });
    },

    setAnnotationCitations() {
      if (!this.counterpointData?.rounds?.length) return;
    },

    annotationGeometryUpdated(geometry) {
      this.geometries[geometry.annotationId] = { ...geometry, cpId: this.cpData._id };
      this.logDebug('annotationGeometryUpdated', {
        annotationId: geometry.annotationId,
        cpId: this.cpData._id,
        top: geometry.top,
        left: geometry.left,
        height: geometry.height,
        width: geometry.width,
      });
      Object.entries(this.geometries)
        .filter(([, v]) => v.top > geometry.top)
        .sort(([, v1], [, v2]) => v1.top - v2.top)
        .slice(0, 1)
        .forEach(([k]) => {
          const annotationRef = this.$refs[`annotation-${k}`]
            ? this.$refs[`annotation-${k}`][0]
            : null;
          if (annotationRef) {
            annotationRef.setAnnotationGeometry();
          }
        });
      this.$forceUpdate();
    },

    annotationParagraphsGeometriesUpdated(annotation, geometries) {
      Object.values(geometries).forEach(geometry => {
        this.geometries[`${annotation._id}_${geometry.nodeKey}`] = {
          ...geometry,
          cpId: this.cpData._id,
        };
        this.logDebug('annotationParagraphGeometryUpdated', {
          annotationId: annotation._id,
          nodeKey: geometry.nodeKey,
          cpId: this.cpData._id,
          top: geometry.top,
          left: geometry.left,
          height: geometry.height,
          width: geometry.width,
        });
      });
    },

    annotationBoxStyle(annotation) {
      if (!this.useExcerpts) {
        this.logDebug('annotationBoxStyle.useExcerptsDisabled', {
          annotationId: annotation._id,
          useExcerpts: this.useExcerpts,
          row: annotation.row,
        });
        return {};
      }

      const rowHeight =
        this.mainPanelView === 'outline' ? this.fixedRowHeight / 2 : this.fixedRowHeight;
      const topValue = (annotation.row - 1) * rowHeight;
      this.logDebug('annotationBoxStyle.useExcerptsEnabled', {
        annotationId: annotation._id,
        row: annotation.row,
        rowHeight,
        topValue,
        mainPanelView: this.mainPanelView,
      });

      return {
        position: 'absolute',
        top: `${topValue}px`,
      };
    },
  },
};
</script>

<style lang="sass" scoped>
.fixed-background
  height: 100vh
  position: fixed
.margin-roundbar
  margin-top: 2rem !important
</style>
